import React, { useCallback, useEffect, useState } from 'react';
import { articlesApi } from '../../services/articlesApi';
import { getToken } from '../auth';
import '../styles/Actualites.css';
import './admin-form.css';

const CATEGORIES = [
  { id: 'all',          lbl: 'Toutes' },
  { id: 'jeunesse',     lbl: 'Jeunesse' },
  { id: 'spirituel',    lbl: 'Spirituel' },
  { id: 'environnement',lbl: 'Environnement' },
  { id: 'sport',        lbl: 'Sport · ASC' },
  { id: 'culture',      lbl: 'Culture' },
  { id: 'mairie',       lbl: 'Mairie' },
];

const EDITABLE_CATS = CATEGORIES.filter((c) => c.id !== 'all');

const EMPTY_FORM = {
  title: '', slug: '', category: 'culture',
  excerpt: '', content: '', author: '',
  imageSeed: '', imageUrl: '',
  publish: false,
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const Actualites = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await articlesApi.adminList(token, {
        category: filter !== 'all' ? filter : undefined,
        q: query || undefined,
        limit: 100,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filter, query]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
    setOpen(true);
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setSaveError(null);
    try {
      const { article } = await articlesApi.adminGet(token, id);
      setForm({
        title: article.title,
        slug: article.slug,
        category: article.category,
        excerpt: article.excerpt || '',
        content: article.content || '',
        author: article.author || '',
        imageSeed: article.imageSeed || '',
        imageUrl: article.imageUrl || '',
        publish: article.published,
      });
      setOpen(true);
    } catch (err) {
      alert("Impossible de charger l'article : " + err.message);
    }
  };

  const close = useCallback(() => {
    setOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        category: form.category,
        excerpt: form.excerpt,
        content: form.content,
        author: form.author,
        imageSeed: form.imageSeed,
        imageUrl: form.imageUrl,
        publish: form.publish,
      };
      if (editingId) await articlesApi.adminUpdate(token, editingId, payload);
      else           await articlesApi.adminCreate(token, payload);
      close();
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement « ${title} » ?`)) return;
    try {
      await articlesApi.adminDelete(token, id);
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  const onTogglePublish = async (id, current) => {
    try {
      await articlesApi.adminUpdate(token, id, { publish: !current });
      await load();
    } catch (err) {
      alert('Action impossible : ' + err.message);
    }
  };

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  const catPillClass = (cat) => {
    if (cat === 'jeunesse') return 'sunset';
    if (cat === 'spirituel') return 'gold';
    if (cat === 'mairie' || cat === 'environnement') return 'ocean';
    return '';
  };

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Actualités</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher un article, un auteur…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
          <button className="top-cta" onClick={openCreate}>
            <span className="plus" /> Publier
          </button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              CMS · {total} article{total > 1 ? 's' : ''}
            </span>
            <h1>Le journal,<br /><span style={{ fontStyle: 'italic' }}>en gestion.</span></h1>
            <p className="sub">Créer, éditer, publier ou retirer un article. Les changements sont visibles immédiatement sur /actualites.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="pill-btn"
              style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}
            >
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span>
            <span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 48 }} />
                <th>Titre</th>
                <th>Catégorie</th>
                <th>Auteur</th>
                <th>Statut</th>
                <th>Date</th>
                <th style={{ width: 120 }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucun article. Cliquez « Publier » pour créer le premier.
                </td></tr>
              )}
              {items.map((a) => (
                <tr key={a.id} onClick={() => openEdit(a.id)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="cell-user">
                      <div
                        className="av sq"
                        style={{
                          width: 36, height: 36,
                          backgroundImage: a.imageUrl
                            ? `url('${a.imageUrl}')`
                            : `url('https://picsum.photos/seed/${a.imageSeed || `cb-news-${a.slug}`}/120/120')`,
                        }}
                      />
                    </div>
                  </td>
                  <td>
                    <b style={{ fontWeight: 500, fontSize: 14 }}>{a.title}</b>
                    <small style={{ display: 'block', marginTop: 3, fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.12em', color: 'var(--char-500)' }}>/{a.slug}</small>
                  </td>
                  <td>
                    <span className={`cat-pill ${catPillClass(a.category)}`}>
                      {CATEGORIES.find((c) => c.id === a.category)?.lbl || a.category}
                    </span>
                  </td>
                  <td>{a.author}</td>
                  <td>
                    <span className={`status-pill ${a.published ? 'live' : 'muted'}`}>
                      {a.published ? 'Publié' : 'Brouillon'}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--char-500)' }}>
                    {fmtDate(a.publishedAt || a.updatedAt)}
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="row-actions" style={{ opacity: 1 }}>
                      <button
                        title={a.published ? 'Dépublier' : 'Publier'}
                        onClick={() => onTogglePublish(a.id, a.published)}
                      >
                        {a.published ? '⏸' : '▶'}
                      </button>
                      <button title="Éditer" onClick={() => openEdit(a.id)}>✎</button>
                      <button title="Supprimer" className="danger" onClick={() => onDelete(a.id, a.title)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} article{total > 1 ? 's' : ''}</span>
            <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ MODALE FORMULAIRE ============ */}
      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? "Éditer l'article" : 'Nouvel article'}</span>
                <h2 className="admin-modal-title">{editingId ? form.title || '—' : 'Publier un article'}</h2>
              </div>
              <button onClick={close} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            <form onSubmit={onSubmit} className="admin-form">
              <Field label="Titre">
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ex : Cambérène inaugure une médiathèque numérique"
                />
              </Field>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
                <Field label="Slug (URL · optionnel)">
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                    placeholder="auto-généré depuis le titre"
                    pattern="[a-z0-9\-]*"
                  />
                </Field>
                <Field label="Catégorie">
                  <select
                    required
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  >
                    {EDITABLE_CATS.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Résumé · 1 à 2 phrases d'accroche">
                <textarea
                  rows={2}
                  maxLength={280}
                  value={form.excerpt}
                  onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                  placeholder="Affiché sur la une et dans la grille"
                />
              </Field>

              <Field label="Contenu · plusieurs paragraphes séparés par une ligne vide">
                <textarea
                  rows={10}
                  required
                  value={form.content}
                  onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                  placeholder="Le corps de l'article. Séparez les paragraphes par une ligne vide."
                />
              </Field>

              <div className="admin-form-row">
                <Field label="Auteur">
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
                    placeholder="Nom complet ou pseudonyme"
                  />
                </Field>
                <Field label="Image · seed Picsum ou URL">
                  <input
                    type="text"
                    value={form.imageSeed || form.imageUrl}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (/^https?:\/\//.test(v)) setForm((f) => ({ ...f, imageUrl: v, imageSeed: '' }));
                      else setForm((f) => ({ ...f, imageSeed: v, imageUrl: '' }));
                    }}
                    placeholder="cb-news-mon-article  ·  ou  https://…"
                  />
                </Field>
              </div>

              <label className="admin-publish-toggle">
                <input
                  type="checkbox"
                  checked={form.publish}
                  onChange={(e) => setForm((f) => ({ ...f, publish: e.target.checked }))}
                />
                <span>
                  {form.publish ? 'Publier immédiatement (visible sur /actualites)' : 'Garder en brouillon'}
                </span>
              </label>

              {saveError && (
                <div className="admin-form-error">{saveError}</div>
              )}

              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Créer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

function Field({ label, children }) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      {children}
    </label>
  );
}

export default Actualites;
